import React, { useState, useRef, useEffect } from 'react';
import { metersService } from '../services/api';
import Pagination from '../components/Pagination';
import {
  Settings, Zap, DollarSign,
  AlertCircle, XCircle, History,
  Loader, Check, X, Copy, Printer, Download, Lock, Unlock,
  Eye, Filter, FileText, Key, ShieldCheck
} from 'lucide-react';

const AdvancedCenter = () => {
  const [selectedMeter, setSelectedMeter] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const tokenRef = useRef(null);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState({
    meter_id: '',
    operation_type: '',
    status: ''
  });
  const totalItems = history.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const paginatedHistory = history.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Modals state
  // ✅ NETTOYAGE : les opérations "à distance" (relevé, crédit compteur, consommation
  // journalière, bascule prépayé/postpayé) sont retirées d'ici car elles existent déjà,
  // en version "à distance", dans la page Contrôle à Distance (Remote). Les garder aux
  // deux endroits ne faisait que dupliquer l'affichage et l'historique.
  // "Max Journalier" est également retiré : confirmé NON SUPPORTÉ par l'API STRON
  // (endpoint inexistant, 404 en dur côté backend — voir stron_service.set_max_daily_usage).
  const [showPowerLimitModal, setShowPowerLimitModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [showDecoderKeyModal, setShowDecoderKeyModal] = useState(false);
  const [showSetDecoderKeyModal, setShowSetDecoderKeyModal] = useState(false);

  const [tokenData, setTokenData] = useState(null);
  const [powerLimit, setPowerLimit] = useState('');
  const [decoderKeyData, setDecoderKeyData] = useState(null);
  const [newDecoderKey, setNewDecoderKey] = useState('');
  const [currentDecoderKey, setCurrentDecoderKey] = useState(null);

  // 🔑 Clé GOMELONG (SGC) — corrige la fiche chez GOMELONG, sans token
  const [showSgcModal, setShowSgcModal] = useState(false);
  const [sgcAll, setSgcAll] = useState({ electricity: [], water: [] });
  const [sgcMeterType, setSgcMeterType] = useState('');
  const [selectedSgc, setSelectedSgc] = useState('');
  const [sgcLoading, setSgcLoading] = useState(false);

  const openSgcModal = async () => {
    if (!selectedMeter) {
      setError('Veuillez sélectionner un compteur');
      return;
    }
    setError('');
    setSgcLoading(true);
    setSelectedSgc('');
    try {
      const res = await metersService.getGomelongSgcList(selectedMeter.trim());
      setSgcAll({ electricity: res.data?.electricity || [], water: res.data?.water || [] });
      setSgcMeterType(res.data?.meter_type || '');
      setShowSgcModal(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Impossible de récupérer les clés GOMELONG');
    } finally {
      setSgcLoading(false);
    }
  };

  const applySgc = async () => {
    if (!selectedSgc) return;
    setSgcLoading(true);
    try {
      const res = await metersService.setGomelongSgc(selectedMeter.trim(), selectedSgc);
      setShowSgcModal(false);
      loadHistory();
      alert(`✅ ${res.data?.message || 'Clé GOMELONG mise à jour'}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Erreur lors de la mise à jour de la clé GOMELONG');
      setShowSgcModal(false);
    } finally {
      setSgcLoading(false);
    }
  };

  // Charger l'historique au montage et quand les filtres changent
  useEffect(() => {
    setCurrentPage(1); // revenir à la page 1
    loadHistory();
  }, [historyFilter]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (historyFilter.meter_id) params.append('meter_id', historyFilter.meter_id);
      if (historyFilter.operation_type) params.append('operation_type', historyFilter.operation_type);
      if (historyFilter.status) params.append('status', historyFilter.status);

      const response = await metersService.getHistory(params.toString());
      // ✅ On n'affiche ici que les opérations propres au Centre Avancé, pour éviter
      // que les opérations "à distance" (gérées et affichées dans Remote) n'apparaissent en double.
      const advancedOps = [
        'clear_credit', 'clear_tamper', 'set_power_limit',
        'get_decoder_key', 'set_decoder_key', 'gomelong_set_sgc'
      ];
      setHistory(response.data.filter(op => advancedOps.includes(op.operation_type)));
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAction = async (action, data = {}) => {
    if (!selectedMeter) {
      setError("Veuillez sélectionner un compteur");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      let response;

      switch (action) {
        case "effacer_le_crédit":
          response = await metersService.clearCredit(selectedMeter);
          break;

        case "effacer_le_sabotage":
          response = await metersService.clearTamper(selectedMeter);
          break;

        case "limite_de_puissance":
          response = await metersService.setPowerLimit(selectedMeter, data.limit);
          break;

        /**
         * 🔵 CONSULTER CLÉ DÉCODEUR
         * ➜ version sécurisée anti page blanche
         */
        case "get_decoder_key": {
          setDecoderKeyData(null);

          const res = await metersService.getDecoderKey(selectedMeter);

          if (!res?.data?.success) {
            setError(res?.data?.message || "Aucune clé décodeur trouvée");
            return;
          }

          if (!res.data.decoder_key) {
            setError("Aucune clé décodeur enregistrée");
            return;
          }

          setDecoderKeyData(res.data);
          setShowDecoderKeyModal(true);
          return;
        }

        /**
         * 🟣 MODIFIER CLÉ DÉCODEUR
         */
        case "set_decoder_key":
          response = await metersService.setDecoderKey(
            selectedMeter,
            data.decoderKey
          );

          if (!response?.data?.success) {
            setError(
              response?.data?.message || "Erreur modification clé décodeur"
            );
            return;
          }
          break;

        default:
          return;
      }

      /**
       * ✅ SUCCÈS GÉNÉRAL
       */
      setResult(response.data);
      loadHistory();

      /**
       * 🎫 SI TOKEN GÉNÉRÉ → OUVRIR MODAL
       */
      if (response.data.token) {
        setTokenData({
          token: response.data.token,
          action,
          meter_id: selectedMeter,
          generation_time:
            response.data.generation_time ||
            new Date().toLocaleString("fr-FR"),
          amount: response.data.amount,
          message: response.data.message,
        });

        setShowTokenModal(true);
      } else {
        alert(`✅ ${response.data.message || "Action réussie !"}`);
      }
    } catch (err) {
      /**
       * 🔴 GESTION ERREUR GLOBALE SÉCURISÉE
       */
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Erreur serveur";

      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      /**
       * 🧠 IMPORTANT : empêche les blocages React
       */
      setLoading(false);
    }
  };

  const copyToken = () => {
    if (tokenData?.token) {
      navigator.clipboard.writeText(tokenData.token);
      alert('✅ Token copié dans le presse-papier !');
    }
  };

  const copyDecoderKey = () => {
    if (decoderKeyData?.decoder_key) {
      navigator.clipboard.writeText(decoderKeyData.decoder_key);
      alert('✅ Clé décodeur copiée !');
    }
  };

  // 🖨️ Imprimer le token
  const printToken = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Token de Recharge - ${tokenData?.meter_id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 600px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 3px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
            .token-box { background: #f3f4f6; border: 2px dashed #2563eb; padding: 30px; text-align: center; 
                        font-size: 28px; font-weight: bold; letter-spacing: 3px; margin: 30px 0; 
                        font-family: 'Courier New', monospace; }
            .info { margin: 15px 0; padding: 10px; background: #f9fafb; border-left: 4px solid #2563eb; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; 
                     text-align: center; font-size: 12px; color: #6b7280; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🎫 TOKEN DE RECHARGE</h1>
            <p>Système de gestion des compteurs</p>
          </div>
          <div class="info"><strong>📟 Compteur:</strong> ${tokenData?.meter_id}</div>
          <div class="info"><strong>⚡ Action:</strong> ${tokenData?.action}</div>
          ${tokenData?.amount ? `<div class="info"><strong>💰 Montant:</strong> ${tokenData.amount} kWh</div>` : ''}
          <div class="token-box">${tokenData?.token}</div>
          <div class="info"><strong>🕒 Généré le:</strong> ${tokenData?.generation_time}</div>
          ${tokenData?.message ? `<div class="info"><strong>📝 Note:</strong> ${tokenData.message}</div>` : ''}
          <div class="footer">
            <p>⚠️ Ce token est à usage unique. Conservez-le en lieu sûr.</p>
            <p>Document généré automatiquement - ${new Date().toLocaleString('fr-FR')}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const downloadToken = () => {
    const content = `
═══════════════════════════════════════════
      TOKEN DE RECHARGE - COMPTEUR
═══════════════════════════════════════════
📟 Compteur: ${tokenData?.meter_id}
⚡ Action: ${tokenData?.action}
${tokenData?.amount ? `💰 Montant: ${tokenData.amount} kWh` : ''}
🎫 TOKEN: ${tokenData?.token}
🕒 Généré le: ${tokenData?.generation_time}
${tokenData?.message ? `📝 Note: ${tokenData.message}` : ''}
═══════════════════════════════════════════
⚠️ Ce token est à usage unique
═══════════════════════════════════════════
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `token_${tokenData?.meter_id}_${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const COLOR_STYLES = {
    yellow: {
      border: "hover:border-yellow-500",
      bg: "bg-yellow-50",
      text: "text-yellow-600",
    },
    red: {
      border: "hover:border-red-500",
      bg: "bg-red-50",
      text: "text-red-600",
    },
    green: {
      border: "hover:border-green-500",
      bg: "bg-green-50",
      text: "text-green-600",
    },
    orange: {
      border: "hover:border-orange-500",
      bg: "bg-orange-50",
      text: "text-orange-600",
    },
    blue: {
      border: "hover:border-blue-500",
      bg: "bg-blue-50",
      text: "text-blue-600",
    },
    purple: {
      border: "hover:border-purple-500",
      bg: "bg-purple-50",
      text: "text-purple-600",
    },
  };

  const ActionCard = ({ icon: Icon, title, color, onClick, disabled = false }) => {
    const styles = COLOR_STYLES[color] || COLOR_STYLES.blue;

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`relative group bg-white rounded-xl shadow-sm border-2 border-transparent 
          ${styles.border} hover:shadow-lg transition-all p-6 text-center 
          disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        <div
          className={`w-20 h-20 mx-auto mb-4 rounded-full ${styles.bg} 
          flex items-center justify-center group-hover:scale-110 transition-transform`}
        >
          <Icon className={`w-10 h-10 ${styles.text}`} />
        </div>

        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </button>
    );
  };

  const getOperationIcon = (type) => {
    const icons = {
      clear_credit: DollarSign,
      clear_tamper: AlertCircle,
      set_power_limit: Zap,
      get_decoder_key: Key,
      set_decoder_key: Key,
      gomelong_set_sgc: ShieldCheck,
    };
    return icons[type] || Settings;
  };

  const printOperationDetail = (operation) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Détails Opération - ${operation.id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 3px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
            .section { margin: 20px 0; padding: 15px; background: #f9fafb; border-left: 4px solid #2563eb; }
            .label { font-weight: bold; color: #374151; display: inline-block; width: 200px; }
            .value { color: #1f2937; }
            .token-box { background: #f3f4f6; border: 2px dashed #2563eb; padding: 20px; text-align: center; 
                        font-size: 20px; font-weight: bold; letter-spacing: 2px; margin: 20px 0; 
                        font-family: 'Courier New', monospace; }
            .status-success { color: #10b981; font-weight: bold; }
            .status-failed { color: #ef4444; font-weight: bold; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; 
                     font-size: 12px; color: #6b7280; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📋 DÉTAILS DE L'OPÉRATION</h1>
            <p>Référence: ${operation.id}</p>
          </div>
          
          <div class="section">
            <h2>Informations Générales</h2>
            <p><span class="label">Compteur:</span> <span class="value">${operation.meter_id}</span></p>
            <p><span class="label">Type d'opération:</span> <span class="value">${operation.operation_type}</span></p>
            <p><span class="label">Statut:</span> <span class="value status-${operation.status}">${operation.status.toUpperCase()}</span></p>
            <p><span class="label">Date:</span> <span class="value">${new Date(operation.created_at).toLocaleString('fr-FR')}</span></p>
          </div>
          
          ${operation.token ? `
          <div class="section">
            <h2>Token Généré</h2>
            <div class="token-box">${operation.token}</div>
            ${operation.generation_time ? `<p><span class="label">Généré le:</span> <span class="value">${operation.generation_time}</span></p>` : ''}
          </div>
          ` : ''}
          
          <div class="section">
            <h2>Opérateur</h2>
            <p><span class="label">Utilisateur:</span> <span class="value">${operation.username}</span></p>
            <p><span class="label">ID Utilisateur:</span> <span class="value">${operation.user_id}</span></p>
            ${operation.reason ? `<p><span class="label">Raison:</span> <span class="value">${operation.reason}</span></p>` : ''}
          </div>
          
          ${operation.power_limit || operation.amount ? `
          <div class="section">
            <h2>Paramètres</h2>
            ${operation.power_limit ? `<p><span class="label">Limite de puissance:</span> <span class="value">${operation.power_limit} W</span></p>` : ''}
            ${operation.amount ? `<p><span class="label">Montant:</span> <span class="value">${operation.amount} kWh</span></p>` : ''}
          </div>
          ` : ''}
          
          ${operation.error_message ? `
          <div class="section" style="border-left-color: #ef4444;">
            <h2 style="color: #ef4444;">Message d'Erreur</h2>
            <p class="value">${operation.error_message}</p>
          </div>
          ` : ''}
          
          <div class="footer">
            <p>Document généré automatiquement - ${new Date().toLocaleString('fr-FR')}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getOperationLabel = (type) => {
    const labels = {
      clear_credit: 'Effacer Crédit',
      clear_tamper: 'Effacer Tamper',
      set_power_limit: 'Limite Puissance',
      get_decoder_key: 'Consulter Clé Décodeur',
      set_decoder_key: 'Modifier Clé Décodeur',
      gomelong_set_sgc: 'Clé GOMELONG corrigée',
    };
    return labels[type] || type;
  };
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center gap-3">
          <Settings className="w-8 h-8" />
          <div>
            <h1 className="text-2xl font-bold">Centre Avancé</h1>
            <p className="text-blue-100 text-sm mt-1">
              Paramètres avancés et opérations sur les compteurs
            </p>
          </div>
        </div>
      </div>

      {/* Sélection du compteur */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Sélectionner un compteur *
        </label>
        <input
          type="text"
          value={selectedMeter}
          onChange={(e) => setSelectedMeter(e.target.value)}
          placeholder="Entrer le numéro de compteur (ex: 58100000001)"
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-mono"
        />
        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}
      </div>

      {/* Actions principales */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Actions de Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ActionCard icon={Zap} title="Définir Limite de Puissance" color="yellow"
            onClick={() => setShowPowerLimitModal(true)} disabled={!selectedMeter} />
          <ActionCard icon={DollarSign} title="Effacer le Crédit" color="green"
            onClick={() => handleAction('effacer_le_crédit')} disabled={!selectedMeter} />
          <ActionCard icon={XCircle} title="Effacer le Sabotage" color="orange"
            onClick={() => handleAction('effacer_le_sabotage')} disabled={!selectedMeter} />
          <ActionCard icon={Key} title="Consulter Clé Décodeur" color="blue"
            onClick={() => handleAction('get_decoder_key')} disabled={!selectedMeter} />

          <ActionCard icon={Key} title="Modifier Clé Décodeur" color="purple"
            onClick={async () => {
              if (!selectedMeter) {
                setError('Veuillez sélectionner un compteur');
                return;
              }

              // ✅ 1. D'abord récupérer la clé actuelle
              setLoading(true);
              try {
                const response = await metersService.getDecoderKey(selectedMeter);

                if (response.data.success && response.data.decoder_key) {
                  setCurrentDecoderKey(response.data.decoder_key);
                } else {
                  setCurrentDecoderKey(null);
                }

                // ✅ 2. Puis ouvrir le modal
                setShowSetDecoderKeyModal(true);

              } catch (err) {
                console.error('Erreur récupération clé:', err);
                setCurrentDecoderKey(null);
                setShowSetDecoderKeyModal(true); // Ouvrir quand même
              } finally {
                setLoading(false);
              }
            }}
            disabled={!selectedMeter}
          />

          <ActionCard icon={ShieldCheck} title="Clé GOMELONG (token rejeté)" color="red"
            onClick={openSgcModal} disabled={!selectedMeter || sgcLoading} />
        </div>
        <p className="text-xs text-gray-500 mt-3">
          🔑 <strong>Clé GOMELONG</strong> : à utiliser quand un compteur GOMELONG/HEXING <strong>rejette</strong> les tokens.
          Corrige la clé utilisée par GOMELONG — aucun token à saisir sur le compteur.
        </p>
      </div>

      {/* Modal Clé GOMELONG */}
      {showSgcModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-red-600 to-rose-700 p-6 rounded-t-2xl text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-8 h-8" />
                  <div>
                    <h2 className="text-xl font-bold">Clé GOMELONG</h2>
                    <p className="text-red-100 text-xs mt-1">
                      Compteur {selectedMeter} · {sgcMeterType === 'water' ? '💧 Eau' : '⚡ Électricité'}
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowSgcModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700">
                Choisissez la clé programmée en usine dans ce compteur. GOMELONG l'utilisera pour
                tous les prochains tokens.
              </p>

              {['electricity', 'water'].map((type) => {
                const keys = sgcAll[type] || [];
                const selectable = type === sgcMeterType;
                return (
                  <div key={type} className={selectable ? '' : 'opacity-60'}>
                    <p className="text-sm font-bold text-gray-800 mb-2">
                      {type === 'water' ? '💧 Clés eau' : '⚡ Clés électricité'}
                      {!selectable && <span className="ml-2 text-xs font-normal text-gray-500">(autre type de compteur — non applicable)</span>}
                    </p>
                    {keys.length === 0 ? (
                      <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg p-2">
                        Aucune clé trouvée chez GOMELONG.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {keys.map((k) => (
                          <label
                            key={`${type}-${k.sgc_id}`}
                            className={`flex items-center gap-3 p-3 rounded-lg border-2 transition ${
                              !selectable
                                ? 'border-gray-100 cursor-not-allowed'
                                : selectedSgc === k.sgc_id
                                  ? 'border-red-500 bg-red-50 cursor-pointer'
                                  : 'border-gray-200 hover:border-red-300 cursor-pointer'
                            }`}
                          >
                            <input
                              type="radio"
                              name="sgc"
                              value={k.sgc_id}
                              disabled={!selectable}
                              checked={selectable && selectedSgc === k.sgc_id}
                              onChange={() => setSelectedSgc(k.sgc_id)}
                            />
                            <div>
                              <p className="font-semibold text-gray-900">Clé {k.sgc_name} — révision {k.krn}</p>
                              <p className="text-xs text-gray-500 font-mono">ID GOMELONG : {k.sgc_id}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-xs text-yellow-800">
                Après la mise à jour, générez un <strong>nouveau</strong> token de recharge (petit montant) et testez-le.
                S'il est encore rejeté, revenez ici et essayez l'autre révision.
              </div>

              <div className="flex gap-3">
                <button
                  onClick={applySgc}
                  disabled={!selectedSgc || sgcLoading}
                  className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2"
                >
                  {sgcLoading && <Loader className="w-4 h-4 animate-spin" />}
                  Appliquer
                </button>
                <button
                  onClick={() => setShowSgcModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Historique */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <History className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Historique des Opérations</h2>
          </div>
          <button
            onClick={loadHistory}
            disabled={historyLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {historyLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />}
            Actualiser
          </button>
        </div>

        {/* Filtres */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <input
            type="text"
            placeholder="Filtrer par compteur"
            value={historyFilter.meter_id}
            onChange={(e) => setHistoryFilter({ ...historyFilter, meter_id: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={historyFilter.operation_type}
            onChange={(e) => setHistoryFilter({ ...historyFilter, operation_type: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les types</option>
            <option value="clear_credit">Effacer Crédit</option>
            <option value="clear_tamper">Effacer Tamper</option>
            <option value="set_power_limit">Limite Puissance</option>
            <option value="get_decoder_key">Consulter Clé Décodeur</option>
            <option value="set_decoder_key">Modifier Clé Décodeur</option>
            <option value="gomelong_set_sgc">Clé GOMELONG corrigée</option>
          </select>
          <select
            value={historyFilter.status}
            onChange={(e) => setHistoryFilter({ ...historyFilter, status: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les statuts</option>
            <option value="success">Réussi</option>
            <option value="failed">Échoué</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Compteur</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Opération</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Utilisateur</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Token</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {history.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p>Aucune opération enregistrée</p>
                  </td>
                </tr>
              ) : (
                paginatedHistory.map((op) => {
                  const Icon = getOperationIcon(op.operation_type);
                  return (
                    <tr key={op.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(op.created_at).toLocaleString('fr-FR')}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">{op.meter_id}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-gray-900">{getOperationLabel(op.operation_type)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{op.username}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{op.token}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${op.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                          {op.status === 'success' ? '✓ Réussi' : '✗ Échoué'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedOperation(op);
                              setShowDetailModal(true);
                            }}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Voir détails"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => printOperationDetail(op)}
                            className="p-1 text-purple-600 hover:bg-purple-50 rounded"
                            title="Imprimer"
                          >
                            <Printer className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          )}
        </div>
      </div>

      {/* Modal Détails Opération */}
      {showDetailModal && selectedOperation && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-t-2xl text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8" />
                  <div>
                    <h2 className="text-2xl font-bold">Détails de l'Opération</h2>
                    <p className="text-blue-100 text-sm mt-1">Référence: {selectedOperation.id}</p>
                  </div>
                </div>
                <button onClick={() => setShowDetailModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 font-semibold mb-1">Compteur</p>
                  <p className="text-lg font-mono font-bold text-blue-900">{selectedOperation.meter_id}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-purple-600 font-semibold mb-1">Type</p>
                  <p className="text-lg font-bold text-purple-900">{getOperationLabel(selectedOperation.operation_type)}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 font-semibold mb-1">Date</p>
                  <p className="text-base font-mono text-gray-900">
                    {new Date(selectedOperation.created_at).toLocaleString('fr-FR')}
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${selectedOperation.status === 'success' ? 'bg-green-50' : 'bg-red-50'
                  }`}>
                  <p className={`text-sm font-semibold mb-1 ${selectedOperation.status === 'success' ? 'text-green-600' : 'text-red-600'
                    }`}>Statut</p>
                  <p className={`text-lg font-bold ${selectedOperation.status === 'success' ? 'text-green-900' : 'text-red-900'
                    }`}>
                    {selectedOperation.status === 'success' ? '✓ Réussi' : '✗ Échoué'}
                  </p>
                </div>
              </div>

              {selectedOperation.token && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-3 border-blue-300 rounded-xl p-4">
                  <p className="text-sm font-bold text-gray-700 mb-2">Token Généré</p>
                  <div className="text-center font-mono text-xl font-bold text-blue-900 break-all p-3 bg-white bg-opacity-60 rounded-lg">
                    {selectedOperation.token}
                  </div>
                </div>
              )}

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-bold text-gray-700 mb-2">Opérateur</p>
                <div className="space-y-1">
                  <p className="text-sm"><span className="font-semibold">Utilisateur:</span> {selectedOperation.username}</p>
                  {selectedOperation.reason && (
                    <p className="text-sm"><span className="font-semibold">Raison:</span> {selectedOperation.reason}</p>
                  )}
                </div>
              </div>

              {(selectedOperation.power_limit || selectedOperation.amount) && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm font-bold text-gray-700 mb-2">Paramètres</p>
                  <div className="space-y-1">
                    {selectedOperation.power_limit && (
                      <p className="text-sm"><span className="font-semibold">Limite de puissance:</span> {selectedOperation.power_limit} W</p>
                    )}
                    {selectedOperation.amount && (
                      <p className="text-sm"><span className="font-semibold">Montant:</span> {selectedOperation.amount} kWh</p>
                    )}
                  </div>
                </div>
              )}

              {selectedOperation.error_message && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <p className="text-sm font-bold text-red-700 mb-2">Message d'Erreur</p>
                  <p className="text-sm text-red-600">{selectedOperation.error_message}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => printOperationDetail(selectedOperation)}
                  className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-semibold"
                >
                  <Printer className="w-5 h-5" />
                  Imprimer
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🎉 MODAL TOKEN */}
      {showTokenModal && tokenData && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 rounded-t-2xl text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <Check className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Token généré avec succès !</h2>
                    <p className="text-green-100 text-sm mt-1">Compteur: {tokenData.meter_id}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTokenModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600 font-semibold mb-1">Action effectuée</p>
                  <p className="text-lg font-bold text-blue-900">{tokenData.action}</p>
                </div>

                {tokenData.amount && (
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <p className="text-sm text-purple-600 font-semibold mb-1">Montant</p>
                    <p className="text-lg font-bold text-purple-900">{tokenData.amount} kWh</p>
                  </div>
                )}

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 col-span-2">
                  <p className="text-sm text-gray-600 font-semibold mb-1">Généré le</p>
                  <p className="text-base font-mono text-gray-900">{tokenData.generation_time}</p>
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  Votre Token de Recharge
                </label>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-3 border-blue-300 rounded-xl p-6 relative overflow-hidden">
                  <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-0 left-0 w-full h-full"
                      style={{ backgroundImage: 'repeating-linear-gradient(45deg, #2563eb 0, #2563eb 1px, transparent 0, transparent 50%)', backgroundSize: '10px 10px' }}></div>
                  </div>

                  <div
                    ref={tokenRef}
                    className="relative text-center font-mono text-2xl md:text-3xl font-bold text-blue-900 break-all tracking-wider select-all p-4 bg-white bg-opacity-60 rounded-lg"
                  >
                    {tokenData.token}
                  </div>
                </div>

                {tokenData.message && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> {tokenData.message}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={copyToken}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold shadow-md hover:shadow-lg"
                >
                  <Copy size={20} />
                  Copier
                </button>

                <button
                  onClick={printToken}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-semibold shadow-md hover:shadow-lg"
                >
                  <Printer size={20} />
                  Imprimer
                </button>

                <button
                  onClick={downloadToken}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold shadow-md hover:shadow-lg"
                >
                  <Download size={20} />
                  Télécharger
                </button>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-orange-800">
                    <p className="font-semibold mb-1">⚠️ Important</p>
                    <p>Ce token est à usage <strong>unique</strong>. Conservez-le en lieu sûr jusqu'à son utilisation sur le compteur.</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowTokenModal(false)}
                className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-semibold"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      {showDecoderKeyModal && decoderKeyData && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-t-2xl text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Key className="w-8 h-8" />
                  <h2 className="text-xl font-bold">Clé Décodeur</h2>
                </div>
                <button onClick={() => setShowDecoderKeyModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-600 font-semibold mb-2">Compteur</p>
                <p className="text-lg font-mono font-bold text-blue-900">{selectedMeter}</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-3 border-green-300 rounded-xl p-4">
                <p className="text-sm font-bold text-gray-700 mb-2">Clé Décodeur Actuelle</p>
                <div className="text-center font-mono text-2xl font-bold text-green-900 break-all p-3 bg-white bg-opacity-60 rounded-lg">
                  {decoderKeyData.decoder_key}   {/* ✅ Affichage de la clé */}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={copyDecoderKey}
                  className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-semibold"
                >
                  <Copy className="w-5 h-5" />
                  Copier
                </button>
                <button
                  onClick={() => setShowDecoderKeyModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier Clé Décodeur */}
      {showSetDecoderKeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6 rounded-t-2xl text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Key className="w-8 h-8" />
                  <h2 className="text-xl font-bold">Modifier Clé Décodeur</h2>
                </div>
                <button
                  onClick={() => {
                    setShowSetDecoderKeyModal(false);
                    setNewDecoderKey('');
                    setCurrentDecoderKey(null);
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* ✅ AFFICHAGE CLÉ ACTUELLE */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4">
                <p className="text-sm font-bold text-gray-700 mb-2">🔑 Clé Décodeur Actuelle</p>
                {currentDecoderKey ? (
                  <div className="text-center font-mono text-xl font-bold text-blue-900 bg-white bg-opacity-60 rounded-lg p-3">
                    {currentDecoderKey}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 italic py-2">
                    ⚠️ Aucune clé enregistrée
                  </div>
                )}
                <p className="text-xs text-gray-600 mt-2 text-center">
                  Compteur: <span className="font-mono font-semibold">{selectedMeter}</span>
                </p>
              </div>
              {/* ✅ SAISIE NOUVELLE CLÉ */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nouvelle Clé Décodeur (SgcId) *
                </label>
                <input
                  type="text"
                  value={newDecoderKey}
                  onChange={(e) => setNewDecoderKey(e.target.value)}
                  placeholder={currentDecoderKey ? `Actuellement: ${currentDecoderKey}` : "Ex: 1234567890"}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Entrez la nouvelle clé décodeur qui existe dans le système GOMELONG
                </p>
              </div>

              {/* ✅ COMPARAISON VISUELLE (si les deux clés sont présentes) */}
              {currentDecoderKey && newDecoderKey && newDecoderKey !== currentDecoderKey && (
                <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg">
                  <p className="text-sm font-semibold text-purple-900 mb-2">📊 Aperçu du changement</p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono bg-red-100 text-red-700 px-2 py-1 rounded line-through">
                      {currentDecoderKey}
                    </span>
                    <span className="text-gray-500">→</span>
                    <span className="font-mono bg-green-100 text-green-700 px-2 py-1 rounded font-bold">
                      {newDecoderKey}
                    </span>
                  </div>
                </div>
              )}

              {/* ✅ AVERTISSEMENT SI MÊME CLÉ */}
              {currentDecoderKey && newDecoderKey && newDecoderKey === currentDecoderKey && (
                <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg">
                  <p className="text-sm text-orange-800">
                    ⚠️ La nouvelle clé est identique à l'actuelle
                  </p>
                </div>
              )}

              {/* ✅ INFO TOKEN */}
              <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg">
                <p className="text-sm text-purple-800">
                  🎫 Un token STS sera généré pour effectuer ce changement sur le compteur
                </p>
              </div>

              {/* ✅ BOUTONS */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (newDecoderKey) {
                      // Vérifier si la clé est différente
                      if (currentDecoderKey && newDecoderKey === currentDecoderKey) {
                        setError('La nouvelle clé est identique à l\'actuelle');
                        return;
                      }

                      handleAction('set_decoder_key', { decoderKey: newDecoderKey });
                      setShowSetDecoderKeyModal(false);
                      setNewDecoderKey('');
                      setCurrentDecoderKey(null);
                    }
                  }}
                  disabled={!newDecoderKey || (currentDecoderKey && newDecoderKey === currentDecoderKey)}
                  className="flex-1 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition"
                >
                  Générer Token
                </button>
                <button
                  onClick={() => {
                    setShowSetDecoderKeyModal(false);
                    setNewDecoderKey('');
                    setCurrentDecoderKey(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-300 font-semibold transition"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Limite de Puissance */}
      {showPowerLimitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-yellow-600 to-yellow-700 p-6 rounded-t-2xl text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Zap className="w-8 h-8" />
                  <h2 className="text-xl font-bold">Définir Limite de Puissance</h2>
                </div>
                <button onClick={() => setShowPowerLimitModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Limite de puissance (W)
                </label>
                <input
                  type="number"
                  value={powerLimit}
                  onChange={(e) => setPowerLimit(e.target.value)}
                  placeholder="Ex: 5000"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ Cette limite s'appliquera immédiatement au compteur {selectedMeter}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (powerLimit) {
                      handleAction('limite_de_puissance', { limit: parseInt(powerLimit) });
                      setShowPowerLimitModal(false);
                      setPowerLimit('');
                    }
                  }}
                  disabled={!powerLimit}
                  className="flex-1 bg-yellow-600 text-white px-4 py-3 rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  Appliquer
                </button>
                <button
                  onClick={() => {
                    setShowPowerLimitModal(false);
                    setPowerLimit('');
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-6 h-6 flex-shrink-0 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-red-800 mb-1">Erreur</p>
            <p className="text-sm text-red-700 whitespace-pre-wrap">{error}</p>
          </div>
          <button
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AdvancedCenter;